import { Icon, IconColor, Tooltip } from '@dc-shared-web/components/base'
import { Button } from '@dc-shared-web/components/shadcn'
import { useFSTrackEvent } from '@dc-shared-web/hooks'
import { cn } from '@dc-shared-web/lib'
import {
  faThumbsDown,
  faThumbsUp,
} from '@document-crunch/pro-regular-svg-icons'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'

type FeedbackType = 'positive' | 'negative'

export function AIFeedback({
  positiveTooltip = 'Good response',
  negativeTooltip = 'Bad response',
  onPositiveFeedback,
  onNegativeFeedback,
  value,
  iconSize = 'lg',
  onFeedbackChange,
  disabled = false,
  containerClassName,
  buttonClassName,
  iconClassName,
  feedbackContext: {
    pageCategory = 'Crunch View',
    subpageCategory,
    entityId,
    entityType = 'crunch',
  },
}: {
  positiveTooltip?: string
  negativeTooltip?: string
  onPositiveFeedback: () => void
  onNegativeFeedback: () => void
  value?: FeedbackType
  iconSize?: SizeProp
  onFeedbackChange?: (type: FeedbackType) => void
  disabled?: boolean
  containerClassName?: string
  buttonClassName?: string
  iconClassName?: string
  feedbackContext: {
    pageCategory?: string
    subpageCategory?: string
    entityId?: string
    entityType?: string
  }
}) {
  const { trackEvent } = useFSTrackEvent()
  const handleFeedback = (type: FeedbackType) => {
    trackEvent('AIFeedback', {
      name: `${pageCategory} - ${subpageCategory}`,
      title: document.title,
      path: window.location.pathname,
      page_category: pageCategory,
      subpage_category: subpageCategory,
      entity_id: entityId,
      entity_type: entityType,
      feedback_type: type,
    })

    onFeedbackChange?.(type)
    if (type === 'positive') {
      onPositiveFeedback()
    } else {
      onNegativeFeedback()
    }
  }

  const useNewButton =
    typeof window !== 'undefined' && window.__VSS_UPDATES__?.buttonEnabled

  return (
    <div
      className={cn('tw-flex', containerClassName)}
      data-testid='ai-feedback-container'
    >
      <Tooltip content={positiveTooltip} withArrow>
        <Button
          className={cn(
            useNewButton
              ? undefined
              : 'tw-bg-transparent hover:tw-bg-primary-100',
            value === 'positive' ? 'tw-bg-primary-200' : undefined,
            buttonClassName
          )}
          variant={useNewButton ? 'ghost' : undefined}
          onClick={() => {
            handleFeedback('positive')
          }}
          disabled={disabled}
          data-testid='ai-feedback-positive-button'
          data-selected={value === 'positive'}
        >
          <Icon
            className={
              useNewButton ? undefined : cn('tw-text-blue-600', iconClassName)
            }
            color={useNewButton ? IconColor.tertiary : undefined}
            icon={faThumbsUp}
            size={iconSize}
            data-testid='ai-feedback-positive-icon'
          />
        </Button>
      </Tooltip>
      <Tooltip content={negativeTooltip} withArrow>
        <Button
          className={cn(
            useNewButton
              ? undefined
              : 'tw-bg-transparent hover:tw-bg-primary-100',
            value === 'negative' ? 'tw-bg-primary-200' : undefined,
            buttonClassName
          )}
          variant={useNewButton ? 'ghost' : undefined}
          onClick={() => {
            handleFeedback('negative')
          }}
          disabled={disabled}
          data-testid='ai-feedback-negative-button'
          data-selected={value === 'negative'}
        >
          <Icon
            className={
              useNewButton ? undefined : cn('tw-text-blue-600', iconClassName)
            }
            color={useNewButton ? IconColor.tertiary : undefined}
            icon={faThumbsDown}
            size={iconSize}
            data-testid='ai-feedback-negative-icon'
          />
        </Button>
      </Tooltip>
    </div>
  )
}
